import { makeStyles } from '@material-ui/core'
import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import DateTimePicker from '../../../atoms/DateTimePicker'
import { ICON_NAMES } from '../../../../constants'
import Icon from '../../../atoms/Icon'
import { useHolidays } from '../../../../api/coreData'
import { disableHolidaysAndWeekends } from '../../../../utils'
import MuiPickersUtcUtilsProvider from '../../../../providers/MuiPickersUtcUtilsProvider'
import Skeleton from '../../../atoms/Skeleton'

const useStyles = makeStyles(() => ({
  datePickerContainer: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    cursor: 'pointer',
    '& svg': {
      marginLeft: 'auto'
    },
    '& span:first-child': {
      minWidth: '5rem',
      padding: '1rem'
    }
  },
  outlined: {
    background: '#FFFFFF',
    border: 'var(--border-neutral-emphasis)',
    borderRadius: 'var(--border-radius-sm)'
  },
  outlinedRounded: {
    background: '#FFFFFF',
    border: 'var(--border-neutral-thin)',
    borderRadius: 'var(--border-radius-md)'
  },
  label: {
    color: '#141929',
    lineHeight: '1rem',
    paddingBottom: '0',
    fontWeight: 'bold',
    fontSize: '1rem !important'
  },
  input: {
    width: '8rem'
  },
  icon: {
    zIndex: 2,
    position: 'absolute',
    inset: '0.85rem 1rem 1rem'
  },
  datePicker: {
    zIndex: 2,
    width: '100%',
    padding: '0.85rem 2rem 0.85rem 0'
  }
}))

const DateSelector = ({
  value,
  format,
  variant,
  onChange,
  className,
  containerClassName,
  prefixLabel,
  isLoading,
  disabledDateRange,
  skeletonProps
}) => {
  const classes = useStyles()
  const { data: holidays } = useHolidays()

  const cn = useMemo(() => {
    return clsx(classes.datePickerContainer, {
      [classes.outlined]: variant === 'outlined',
      [classes.outlinedRounded]: variant === 'outlined-rounded',
      [containerClassName]: Boolean(containerClassName)
    })
  }, [
    classes.datePickerContainer,
    classes.outlined,
    classes.outlinedRounded,
    containerClassName,
    variant
  ])

  const shouldDisableDate = useCallback(
    (date) => disableHolidaysAndWeekends(date, holidays),
    [holidays]
  )

  return (
    <div className={cn}>
      {prefixLabel && <span className={classes.label}>{prefixLabel}</span>}
      {!isLoading ? (
        <MuiPickersUtcUtilsProvider>
          <DateTimePicker
            format={format}
            disableFuture
            value={value}
            onChange={onChange}
            className={clsx(classes.datePicker, {
              [className]: Boolean(className)
            })}
            disabledDateRange={disabledDateRange}
            shouldDisableDate={shouldDisableDate}
            inputClassName={clsx(classes.label, classes.input)}
          />
        </MuiPickersUtcUtilsProvider>
      ) : (
        <Skeleton {...skeletonProps} />
      )}
      <Icon
        additionalClasses={classes.icon}
        name={ICON_NAMES.calendar}
        customSize='1.25rem'
      />
    </div>
  )
}

DateSelector.propTypes = {
  format: PropTypes.string,
  prefixLabel: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  variant: PropTypes.oneOf(['outlined', 'outlined-rounded']),
  disabledDateRange: PropTypes.shape({
    min: PropTypes.string,
    max: PropTypes.string
  }),
  isLoading: PropTypes.bool,
  className: PropTypes.string,
  containerClassName: PropTypes.string,
  skeletonProps: PropTypes.object
}

DateSelector.defaultProps = {
  variant: 'outlined',
  format: 'MM/DD/YYYY',
  disabledDateRange: undefined,
  isLoading: false,
  className: undefined,
  skeletonProps: {
    height: '2.85rem'
  }
}

export default DateSelector
